import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError, timeout } from 'rxjs';
import { IDENTITY_VERIFICATION_REQUEST_TIMEOUT_IN_MS } from 'src/app/pages/identity-verification/constants/common-constants';
import { Field, Param, Source } from 'src/app/pages/identity-verification/models/identity-verification.types';
import { VerificationStatus } from 'src/app/pages/identity-verification/types/identity-verification.types';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class IdentityVerificationApiInterfaceService {
  constructor(
    private httpClient: HttpClient,
    private authTokenService: AuthTokenService,
  ) {}

  /**
   * Retrieves the verification status of a user.
   * Makes an HTTP GET request to fetch the verification status for the user with the given UUID.
   *
   * @param userUuid - The UUID of the user whose verification status is being retrieved.
   */
  getVerificationStatus(userUuid: string): Observable<VerificationStatus> {
    return this.getRequest(`user/${userUuid}/verifications`, []).pipe(
      timeout(IDENTITY_VERIFICATION_REQUEST_TIMEOUT_IN_MS),
      map((response) => response.data as VerificationStatus),
      catchError((error: unknown) => {
        // Observability: Add monitoring for identity verification service failure.
        console.error(error, 'Identity Verification: IdentityVerificationApiInterfaceService.getVerificationStatus$');
        return throwError(() => error);
      }),
    );
  }

  /**
   * Generic helper method to perform GET requests to the Identity Verification service.
   *
   * @param endpoint - The specific endpoint in the service to call.
   * @param params - Any router params to append to the query.
   */
  private getRequest(
    endpoint: string,
    params: Param[] = [],
  ): Observable<{ data: Source[] | Field[] | VerificationStatus }> {
    const paramString = params.map((param) => `${param.key}=${param.value}`).join(',');
    const finalParamString = paramString ? `?${paramString}` : '';

    return this.httpClient.get<{ data: VerificationStatus }>(
      `${this.getServiceURL()}/identity_verification/api/${endpoint}${finalParamString}`,
      {
        headers: this.generateAuthHeaders(this.authTokenService.accessToken),
      },
    );
  }

  /**
   * Gets a list of all possible fields for a specific ID document (aka source) from the Identity Verification microservice.
   *
   * @param source - The name of the ID document from Identity Verification microservice.
   * @returns - The Response or ErrorResponse from the server.
   */
  fetchFields(source: string): Observable<Field[]> {
    return this.getRequest(`get_fields/${source}`).pipe(
      timeout(IDENTITY_VERIFICATION_REQUEST_TIMEOUT_IN_MS),
      map((response) => response.data as Field[]),
      catchError((error) => {
        // Observability: Add monitoring for identity verification service failure.
        console.error(error, 'Identity Verification: IdentityVerificationApiInterfaceService.fetchFields$');
        throw error;
      }),
    );
  }

  /**
   * Gets the list of possible ID documents (aka sources) from the Identity Verification microservice.
   *
   * @returns - The Response or ErrorResponse from the server.
   */
  fetchSources(): Observable<Source[]> {
    return this.getRequest('get_sources').pipe(
      timeout(IDENTITY_VERIFICATION_REQUEST_TIMEOUT_IN_MS),
      map((response) => response.data as Source[]),
      catchError((error) => {
        // Observability: Add monitoring for identity verification service failure.
        console.error(error, 'Identity Verification: IdentityVerificationApiInterfaceService.fetchSources$');

        throw error;
      }),
    );
  }

  /**
   * Gets the specific endpoint of the Identity Verification service per the environment.
   *
   * @returns The base URL where the service can be found.
   */
  private getServiceURL(): string {
    if (environment.environmentName === 'release' && environment.identityVerificationServiceConfig.useLocal) {
      return `http://localhost:${environment.identityVerificationServiceConfig.port}${environment.servicePath}`;
    }
    return environment.serviceUrl();
  }

  private generateAuthHeaders = (token: string): HttpHeaders => {
    if (token) {
      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
      return headers;
    }
    return null;
  };
}
